import { DataTable } from '@chiroup/components/ui/data-table';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  BookmarkIcon,
  ChartBarIcon,
  EyeIcon,
  ShareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import useResearchAnalytics from '../../hooks/useResearchAnalytics';
import { ResearchAnalyticsArticle } from '../../services/research/researchAnalytics.service';
import Modal from '../common/Modal';
import Header from '../layout/Header';
import ResearchAnalyticsDetailModal from './ResearchAnalyticsDetailModal';
import ResearchAnalyticsFilters from './ResearchAnalyticsFilters';

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4">
    <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}>{icon}</div>
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
        {value.toLocaleString()}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </div>
);

type SortHeaderProps = {
  field: string;
  label: string;
  icon?: React.ReactNode;
  activeSort: string;
  activeSortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
};

const SortHeader: React.FC<SortHeaderProps> = ({
  field,
  label,
  icon,
  activeSort,
  activeSortDir,
  onSort,
}) => {
  const isActive = activeSort === field;
  return (
    <button
      onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
    >
      {icon}
      {label}
      {isActive ? (
        activeSortDir === 'desc' ? (
          <ArrowDownIcon className="w-3 h-3 text-primary-500" />
        ) : (
          <ArrowUpIcon className="w-3 h-3 text-primary-500" />
        )
      ) : (
        <ArrowsUpDownIcon className="w-3 h-3 opacity-30" />
      )}
    </button>
  );
};

const ResearchAnalytics: React.FC = () => {
  const {
    filters,
    summary,
    articles,
    isArticlesLoading,
    setSearch,
    setDateRange,
    setSort,
    setPage,
    setPageSize,
    setMetricFilters,
  } = useResearchAnalytics();

  const [selectedArticle, setSelectedArticle] = useState<ResearchAnalyticsArticle | null>(null);

  const handleSort = useCallback(
    (field: string) => {
      if (filters.sort === field) {
        setSort(field, filters.sortDir === 'desc' ? 'asc' : 'desc');
      } else {
        setSort(field, 'desc');
      }
    },
    [filters.sort, filters.sortDir, setSort]
  );

  const columns: ColumnDef<ResearchAnalyticsArticle>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: () => (
          <SortHeader
            field="title"
            label="Article"
            activeSort={filters.sort}
            activeSortDir={filters.sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <div className="max-w-md">
            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {row.original.title}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {row.original.createDate}
            </p>
          </div>
        ),
        enableSorting: false,
        size: 400,
      },
      {
        accessorKey: 'viewCount',
        header: () => (
          <SortHeader
            field="viewCount"
            label="Views"
            icon={<EyeIcon className="w-3.5 h-3.5" />}
            activeSort={filters.sort}
            activeSortDir={filters.sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums text-gray-700 dark:text-gray-300">
            {row.original.viewCount}
          </span>
        ),
        enableSorting: false,
        meta: { align: 'right' as const },
        size: 90,
      },
      {
        accessorKey: 'saveCount',
        header: () => (
          <SortHeader
            field="saveCount"
            label="Saves"
            icon={<BookmarkIcon className="w-3.5 h-3.5" />}
            activeSort={filters.sort}
            activeSortDir={filters.sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums text-gray-700 dark:text-gray-300">
            {row.original.saveCount}
          </span>
        ),
        enableSorting: false,
        meta: { align: 'right' as const },
        size: 90,
      },
      {
        accessorKey: 'dismissCount',
        header: () => (
          <SortHeader
            field="dismissCount"
            label="Dismissed"
            icon={<XCircleIcon className="w-3.5 h-3.5" />}
            activeSort={filters.sort}
            activeSortDir={filters.sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums text-gray-700 dark:text-gray-300">
            {row.original.dismissCount}
          </span>
        ),
        enableSorting: false,
        meta: { align: 'right' as const },
        size: 100,
      },
      {
        accessorKey: 'shareCount',
        header: () => (
          <SortHeader
            field="shareCount"
            label="Shares"
            icon={<ShareIcon className="w-3.5 h-3.5" />}
            activeSort={filters.sort}
            activeSortDir={filters.sortDir}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums text-gray-700 dark:text-gray-300">
            {row.original.shareCount}
          </span>
        ),
        enableSorting: false,
        meta: { align: 'right' as const },
        size: 90,
      },
    ],
    [filters.sort, filters.sortDir, handleSort]
  );

  return (
    <>
      <Header title="Research Review Analytics" />

      <div className="px-4 sm:px-6 pb-8 mt-4">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard
            label="Total Articles"
            value={summary?.totalArticles || 0}
            icon={<ChartBarIcon className="w-5 h-5 text-blue-600" />}
            color="bg-blue-50 dark:bg-blue-900/20"
          />
          <StatCard
            label="Total Views"
            value={summary?.totalViews || 0}
            icon={<EyeIcon className="w-5 h-5 text-green-600" />}
            color="bg-green-50 dark:bg-green-900/20"
          />
          <StatCard
            label="Total Saves"
            value={summary?.totalSaves || 0}
            icon={<BookmarkIcon className="w-5 h-5 text-primary-600" />}
            color="bg-primary-50 dark:bg-primary-900/20"
          />
          <StatCard
            label="Total Dismissed"
            value={summary?.totalDismisses || 0}
            icon={<XCircleIcon className="w-5 h-5 text-amber-600" />}
            color="bg-amber-50 dark:bg-amber-900/20"
          />
          <StatCard
            label="Total Shares"
            value={summary?.totalShares || 0}
            icon={<ShareIcon className="w-5 h-5 text-purple-600" />}
            color="bg-purple-50 dark:bg-purple-900/20"
          />
        </div>

        {/* Articles table */}
        <DataTable
          columns={columns}
          data={articles?.data || []}
          loading={isArticlesLoading && !articles}
          fetching={isArticlesLoading && !!articles}
          toolbar={{
            search: {
              placeholder: 'Search articles...',
              value: filters.search,
              onChange: setSearch,
            },
            rightSlot: (
              <ResearchAnalyticsFilters
                filters={filters.metricFilters}
                onChange={setMetricFilters}
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                onDateChange={setDateRange}
              />
            ),
          }}
          onRowClick={row => setSelectedArticle(row)}
          pagination={
            articles
              ? {
                  manual: true,
                  pageIndex: filters.page,
                  pageSize: filters.pageSize,
                  pageCount: articles.pageCount,
                  totalItems: articles.totalItems,
                  onPageChange: setPage,
                  onPageSizeChange: setPageSize,
                }
              : false
          }
          stickyHeader
          size="compact"
          emptyState={
            <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
              No articles found.
            </div>
          }
        />
      </div>

      {/* Article detail modal */}
      <Modal
        isOpen={selectedArticle !== null}
        close={() => setSelectedArticle(null)}
        header={{ title: selectedArticle?.title || 'Article Details' }}
        size="lg"
        darkBg
      >
        {selectedArticle && (
          <ResearchAnalyticsDetailModal
            newsId={selectedArticle.ID}
            title={selectedArticle.title}
            createDate={selectedArticle.createDate}
            viewCount={selectedArticle.viewCount}
            saveCount={selectedArticle.saveCount}
            dismissCount={selectedArticle.dismissCount}
            shareCount={selectedArticle.shareCount}
          />
        )}
      </Modal>
    </>
  );
};

export default ResearchAnalytics;
